import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import ProductGrid from "@/components/ProductGrid";
import ProductDrawer from "@/components/ProductDrawer";
import { useToast } from "@/hooks/use-toast";
import { MagnifyingGlass, Monitor, SignOut, CircleNotch, Camera, ShoppingCartSimple } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { buildFieldTypeMap, canSeeGT } from "@/lib/fmUtils";
import BarcodeScannerModal from "@/components/BarcodeScannerModal";

const LIMIT = 20;
const LAYOUT = "main products";
const MOBILE_COLUMNS = [
  { key: "photo", label: "Photo" },
  { key: "description", label: "Description" },
  { key: "item no", label: "Item No" },
  { key: "CBM", label: "CBM" },
  { key: "qty", label: "Quantity" },
];

export default function MobileProducts() {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [fields, setFields] = useState([]);
  const [records, setRecords] = useState([]);
  const [foundCount, setFoundCount] = useState(0);
  const [offset, setOffset] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [cartCount, setCartCount] = useState(0);

  const fetchCartCount = useCallback(() => {
    api
      .get("/cart")
      .then(({ data }) => setCartCount((data.items || []).length))
      .catch(() => {});
  }, []);

  useEffect(fetchCartCount, [fetchCartCount]);

  const fieldTypeMap = useMemo(() => buildFieldTypeMap(fields), [fields]);

  useEffect(() => {
    api
      .get(`/fm/layout-fields?layout=${encodeURIComponent(LAYOUT)}`)
      .then(({ data }) => {
        setFields(data?.fields || []);
      })
      .catch(() => {
        setFields([]);
      });
  }, []);

  const fetchRecords = useCallback(
    async (targetOffset, append) => {
      if (append) setLoadingMore(true);
      else setLoading(true);
      try {
        const params = { layout: LAYOUT, offset: targetOffset, limit: LIMIT, sort_field: "CBM", sort_order: "descend" };
        if (search) params.q = search;
        if (showAll) params.show_all = 1;
        const { data } = await api.get("/fm/records", { params });
        const newRecords = data.response?.data || [];
        setRecords((prev) => (append ? [...prev, ...newRecords] : newRecords));
        setFoundCount(data.response?.dataInfo?.foundCount || 0);
      } catch (e) {
        toast({
          title: "Failed to load products",
          description: formatApiErrorDetail(e.response?.data?.detail),
          variant: "destructive",
        });
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [search, showAll, toast]
  );

  useEffect(() => {
    setOffset(1);
    fetchRecords(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, showAll]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
  };

  const handleLoadMore = () => {
    const nextOffset = offset + LIMIT;
    setOffset(nextOffset);
    fetchRecords(nextOffset, true);
  };

  const openRecord = (rec) => {
    setSelectedRecord(rec);
    setDrawerOpen(true);
  };

  const lookupBarcode = useCallback(
    async (rawValue) => {
      setScannerOpen(false);
      setScanning(true);
      try {
        const { data } = await api.get("/fm/barcode-lookup", { params: { barcode: rawValue } });
        if (data.found && data.records.length > 0) {
          openRecord(data.records[0]);
        } else {
          toast({ title: "No product found for this barcode", description: rawValue, variant: "destructive" });
        }
      } catch (e) {
        toast({
          title: "Barcode lookup failed",
          description: formatApiErrorDetail(e.response?.data?.detail),
          variant: "destructive",
        });
      } finally {
        setScanning(false);
      }
    },
    [toast]
  );

  const canLoadMore = records.length < foundCount;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="mobile-products-page">
      <div className="sticky top-0 z-10 border-b border-zinc-300 bg-white">
        <div className="flex items-center justify-between gap-2 px-4 py-3">
          <div>
            <h1 className="font-heading text-base font-semibold tracking-tight text-zinc-900" data-testid="mobile-page-title">
              BetterHome Inventory
            </h1>
            <p className="text-[10px] uppercase tracking-[0.05em] text-zinc-400">Mobile Lookup - {user?.email}</p>
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              variant="outline"
              className="relative rounded-sm border-zinc-300 p-2 h-auto"
              onClick={() => navigate("/mobile/cart")}
              title="Cart"
              data-testid="mobile-cart-button"
            >
              <ShoppingCartSimple size={16} />
              {cartCount > 0 && (
                <span
                  className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-blue-600 px-1 text-[9px] font-bold text-white"
                  data-testid="mobile-cart-count-badge"
                >
                  {cartCount}
                </span>
              )}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="rounded-sm border-zinc-300 p-2 h-auto"
              onClick={() => navigate("/")}
              title="Desktop View"
              data-testid="mobile-desktop-view-button"
            >
              <Monitor size={16} />
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="rounded-sm border-zinc-300 p-2 h-auto"
              onClick={logout}
              title="Logout"
              data-testid="mobile-logout-button"
            >
              <SignOut size={16} />
            </Button>
          </div>
        </div>
        <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 px-4 pb-3">
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={canSeeGT(user) ? "Search item no, GT SKU, barcode..." : "Search item no, barcode..."}
            className="h-11 rounded-sm border-zinc-300 text-sm"
            data-testid="mobile-search-input"
          />
          <Button type="submit" className="h-11 rounded-sm bg-blue-600 px-4 text-white hover:bg-blue-700" data-testid="mobile-search-button">
            <MagnifyingGlass size={18} />
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-11 rounded-sm border-zinc-300 px-4"
            onClick={() => setScannerOpen(true)}
            disabled={scanning}
            data-testid="mobile-scan-barcode-button"
          >
            {scanning ? <CircleNotch size={18} className="animate-spin" /> : <Camera size={18} />}
          </Button>
        </form>
      </div>

      <BarcodeScannerModal open={scannerOpen} onClose={() => setScannerOpen(false)} onDetected={lookupBarcode} />

      <div className="flex items-center justify-between px-4 py-2">
        <span className="text-xs text-zinc-500" data-testid="mobile-found-count">
          {loading ? "Loading..." : `${foundCount} product${foundCount === 1 ? "" : "s"} found`}
        </span>
        <Button
          size="sm"
          variant={showAll ? "default" : "outline"}
          className={`h-8 rounded-sm text-xs ${showAll ? "bg-blue-600 text-white hover:bg-blue-700" : "border-zinc-300"}`}
          onClick={() => setShowAll((v) => !v)}
          data-testid="mobile-show-all-button"
        >
          {showAll ? "Show All: ON" : "Show All"}
        </Button>
      </div>

      <ProductGrid
        layout={LAYOUT}
        columns={MOBILE_COLUMNS}
        records={records}
        fieldTypeMap={fieldTypeMap}
        loading={loading}
        onRowClick={openRecord}
        showHoverPreview={false}
        mobileLayout
        data-testid="mobile-product-grid"
      />

      {!loading && canLoadMore && (
        <div className="flex justify-center p-4">
          <Button
            variant="outline"
            className="rounded-sm border-zinc-300"
            onClick={handleLoadMore}
            disabled={loadingMore}
            data-testid="mobile-load-more-button"
          >
            {loadingMore ? (
              <>
                <CircleNotch size={16} className="mr-1.5 animate-spin" /> Loading...
              </>
            ) : (
              "Load More"
            )}
          </Button>
        </div>
      )}

      <ProductDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        layout={LAYOUT}
        record={selectedRecord}
        fields={fields}
        readOnly
        allowAddToCart
        onCartUpdated={fetchCartCount}
      />
    </div>
  );
}
